'use client';

import React, { useState, useRef, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import {
  validateName,
  validatePhone,
  validateEmail,
  validateRegistration,
  validateEnquiryType,
  validateAll,
  getEmailSuggestion,
} from '@/lib/formValidation';

// ─── Icon helpers ─────────────────────────────────────────────────────────────

function ValidIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#16a34a"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function InvalidIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#CC1A1A"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <circle cx="12" cy="16" r="0.5" fill="#CC1A1A" />
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormValues {
  name: string;
  phone: string;
  email: string;
  registration: string;
  vehicleMake: string;
  enquiryType: string;
  message: string;
  gdpr: boolean;
}

const EMPTY_FORM: FormValues = {
  name: '',
  phone: '',
  email: '',
  registration: '',
  vehicleMake: '',
  enquiryType: '',
  message: '',
  gdpr: false,
};

// ─── FieldWrapper (defined OUTSIDE component to prevent remounting on re-render) ──

interface FieldWrapperProps {
  children: React.ReactNode;
  name: string;
  hintId?: string;
  errorId?: string;
  hint?: string;
  status: 'valid' | 'invalid' | 'idle';
  error?: string;
}

function FieldWrapper({ children, hintId, errorId, hint, status, error }: FieldWrapperProps) {
  return (
    <div>
      <div className="relative">
        {children}
        {status !== 'idle' && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {status === 'valid' ? <ValidIcon /> : <InvalidIcon />}
          </span>
        )}
      </div>
      {hint && status === 'idle' && hintId && (
        <p id={hintId} className="mt-1.5 font-dm-sans text-[12px] text-white/30">
          {hint}
        </p>
      )}
      {error && errorId && (
        <p id={errorId} role="alert" className="mt-1.5 font-dm-sans text-[12px] text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ContactPage() {
  const [formState, setFormState] = useState<FormValues>(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [retryStatus, setRetryStatus] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [orderRef, setOrderRef] = useState<string>('');
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const lastSubmitRef = useRef<number>(0);

  const errorSummaryRef = useRef<HTMLDivElement>(null);
  const validationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Field validator map ────────────────────────────────────────────────────

  const validateField = useCallback((name: string, value: string | boolean): string => {
    switch (name) {
      case 'name': return validateName(value as string);
      case 'phone': return validatePhone(value as string);
      case 'email': return validateEmail(value as string);
      case 'registration': return validateRegistration(value as string);
      case 'enquiryType': return validateEnquiryType(value as string);
      case 'gdpr': return (value as boolean) ? '' : 'You must consent to proceed';
      default: return '';
    }
  }, []);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;

    const finalValue = name === 'registration' && typeof newValue === 'string'
      ? newValue.toUpperCase()
      : newValue;

    // Value update is instant — typing stays responsive
    setFormState((prev) => ({ ...prev, [name]: finalValue }));

    if (name === 'email' && typeof finalValue === 'string') {
      setEmailSuggestion(getEmailSuggestion(finalValue));
    }

    // Validation is debounced 300ms — only runs if field has been touched
    if (touched[name]) {
      if (validationDebounceRef.current) clearTimeout(validationDebounceRef.current);
      validationDebounceRef.current = setTimeout(() => {
        const err = validateField(name, finalValue);
        setErrors((prev) => {
          const next = { ...prev };
          if (err) next[name] = err;
          else delete next[name];
          return next;
        });
      }, 300);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const fieldValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, fieldValue);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const applyEmailSuggestion = () => {
    if (!emailSuggestion) return;
    setFormState((prev) => ({ ...prev, email: emailSuggestion }));
    setEmailSuggestion(null);
    setTouched((prev) => ({ ...prev, email: true }));
    const err = validateEmail(emailSuggestion);
    setErrors((prev) => {
      const next = { ...prev };
      if (err) next.email = err;
      else delete next.email;
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ── Rate limiting ────────────────────────────────────────────────────────
    const now = Date.now();
    if (now - lastSubmitRef.current < 60000) {
      const remaining = Math.ceil((60000 - (now - lastSubmitRef.current)) / 1000);
      setRateLimitMsg(`Please wait ${remaining} seconds before submitting again.`);
      return;
    }
    setRateLimitMsg('');

    // ── Honeypot check ───────────────────────────────────────────────────────
    if (honeypot) {
      // Silently fake success for bots
      setShowModal(true);
      return;
    }

    setTouched({ name: true, phone: true, email: true, registration: true, enquiryType: true, gdpr: true });

    const newErrors = validateAll({
      name: formState.name,
      phone: formState.phone,
      email: formState.email,
      registration: formState.registration,
      enquiryType: formState.enquiryType,
      gdpr: formState.gdpr,
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTimeout(() => {
        errorSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 50);
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setRetryStatus(null);
    lastSubmitRef.current = Date.now();

    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
    const ref = `NL-${Date.now().toString(36).toUpperCase().slice(-6)}`;

    // ── Save to contact_messages Supabase table (independent of email) ────────
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      await supabase.from('contact_messages').insert({
        name: formState.name,
        phone: formState.phone,
        email: formState.email || null,
        message: formState.message || `Enquiry type: ${formState.enquiryType}${formState.registration ? ` | Reg: ${formState.registration}` : ''}${formState.vehicleMake ? ` | Vehicle: ${formState.vehicleMake}` : ''}`,
      });
    } catch {
      // Supabase save failure must not block email
    }

    const MAX_ATTEMPTS = 3;
    const BACKOFF_DELAYS = [1000, 2000, 4000];

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        if (attempt > 1) {
          setRetryStatus(`Sending... (attempt ${attempt} of ${MAX_ATTEMPTS})`);
          await new Promise((resolve) => setTimeout(resolve, BACKOFF_DELAYS[attempt - 2]));
        } else {
          setRetryStatus('Sending...');
        }

        const res = await fetch(`${supabaseUrl}/functions/v1/send-contact-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${supabaseAnonKey}`,
          },
          body: JSON.stringify({
            name: formState.name,
            phone: formState.phone,
            email: formState.email,
            registration: formState.registration,
            vehicleMake: formState.vehicleMake,
            enquiryType: formState.enquiryType,
            message: formState.message,
            referenceNumber: ref,
          }),
        });

        // Don't retry on 4xx client errors (validation errors)
        if (res.status >= 400 && res.status < 500) {
          const data = await res.json();
          throw Object.assign(new Error(data.error || 'Invalid request. Please check your details.'), { isClientError: true });
        }

        const data = await res.json();
        if (!res.ok || data.error) {
          throw new Error(data.error || 'Failed to send enquiry. Please try again.');
        }

        // Success
        setRetryStatus(null);
        setOrderRef(ref);
        setSubmitSuccess(true);
        setTimeout(() => {
          setFormState(EMPTY_FORM);
          setErrors({});
          setTouched({});
          setEmailSuggestion(null);
          setSubmitSuccess(false);
          setShowModal(true);
        }, 500);
        setSubmitting(false);
        return;
      } catch (err) {
        lastError = err as Error;
        // Don't retry on client errors
        if ((err as Error & { isClientError?: boolean }).isClientError) {
          break;
        }
        // If not last attempt, continue to retry
        if (attempt < MAX_ATTEMPTS) {
          continue;
        }
      }
    }

    // All attempts failed
    setRetryStatus(null);
    setSubmitError(
      lastError?.message || 'Something went wrong. Please try again or call us on 0161 400 1687.'
    );
    setSubmitting(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSubmitted(true);
  };

  // ─── Reset form for new submission ─────────────────────────────────────────

  const resetForm = () => {
    setSubmitted(false);
    setShowModal(false);
    setFormState(EMPTY_FORM);
    setErrors({});
    setTouched({});
    setEmailSuggestion(null);
    setOrderRef('');
    setSubmitError(null);
  };

  // ─── Field status helpers ───────────────────────────────────────────────────

  const fieldStatus = (name: string): 'valid' | 'invalid' | 'idle' => {
    if (!touched[name]) return 'idle';
    if (errors[name]) return 'invalid';
    return 'valid';
  };

  const inputClass = (name: string) => {
    const status = fieldStatus(name);
    const base =
      'w-full bg-[#141414] border rounded-lg px-4 py-3.5 text-white font-dm-sans text-[15px] placeholder:text-white/25 focus:outline-none transition-colors min-h-[52px] pr-10';
    if (status === 'valid') return `${base} border-[#16a34a] focus:border-[#16a34a]`;
    if (status === 'invalid') return `${base} border-[#CC1A1A] focus:border-[#CC1A1A]`;
    return `${base} border-white/[0.1] focus:border-[#CC1A1A]`;
  };

  const labelClass = 'block font-dm-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40 mb-2';

  const errorCount = Object.keys(errors).length;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="relative min-h-screen bg-[#0a0a0a]">
      {/* ─── SUCCESS MODAL ─── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-heading-contact"
        >
          <div
            className="relative w-full max-w-md rounded-xl p-8 flex flex-col items-center text-center"
            style={{ background: '#121212', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'rgba(22,163,74,0.15)', border: '2px solid rgba(22,163,74,0.5)' }}
              aria-hidden="true"
            >
              <svg
                width="40" height="40" viewBox="0 0 24 24" fill="none"
                stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: 'checkPop 0.4s ease-out' }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 id="modal-heading-contact" className="font-dm-sans font-black text-white text-2xl mb-3">
              Booking Received!
            </h2>
            <p className="font-dm-sans text-white/60 text-[15px] leading-relaxed mb-4">
              We&apos;ll call you within 1 hour. A courtesy car is reserved for you.
            </p>
            {orderRef && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <span className="font-dm-sans text-[11px] uppercase tracking-[0.2em] text-white/35">Reference</span>
                <span className="font-dm-sans text-[13px] font-bold text-white/80 tracking-wider">{orderRef}</span>
              </div>
            )}
            <button
              onClick={handleCloseModal}
              className="w-full py-3 font-dm-sans font-bold text-[13px] uppercase tracking-[0.15em] text-white transition-all duration-200 rounded-lg"
              style={{ background: '#CC1A1A' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
              onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes checkPop { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      <Header />
      <main id="main-content">
        {/* Hero */}
        <section className="pt-32 pb-16 px-6 text-center" aria-labelledby="contact-hero-heading">
          <div className="max-w-3xl mx-auto">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#CC1A1A]/40 bg-[#CC1A1A]/10 mb-8"
              aria-hidden="true"
            >
              <span className="font-dm-sans text-[11px] font-semibold tracking-[0.18em] uppercase text-[#CC1A1A]">
                Get In Touch
              </span>
            </span>
            <h1
              id="contact-hero-heading"
              className="font-fraunces text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-tight"
            >
              Contact No Limit Auto Body Specialist
            </h1>
            <p className="font-dm-sans text-[17px] text-white/55 leading-relaxed">
              Free estimates, claim enquiries, and technical questions — we&apos;re here to help.
            </p>
          </div>
        </section>

        {/* Two-column layout */}
        <section className="px-6 pb-24" aria-labelledby="contact-form-heading">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* Left — Contact details */}
            <div className="flex flex-col gap-6">
              {/* Phone */}
              <div className="bg-[#141414] border border-white/[0.07] rounded-xl p-6 flex items-start gap-4">
                <div
                  className="w-12 h-12 rounded-lg bg-[#CC1A1A]/10 border border-[#CC1A1A]/20 flex items-center justify-center text-[#CC1A1A] shrink-0"
                  aria-hidden="true"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                  </svg>
                </div>
                <div>
                  <p className="font-dm-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35 mb-1">Phone</p>
                  <a
                    href="tel:01614001687"
                    className="font-fraunces text-xl font-semibold text-white hover:text-[#CC1A1A] transition-colors"
                    aria-label="Call us on 0161 400 1687"
                  >
                    0161 400 1687
                  </a>
                  <p className="font-dm-sans text-[12px] text-white/35 mt-1">Mon – Sat · 8AM – 6PM</p>
                </div>
              </div>

              {/* Email */}
              <div className="bg-[#141414] border border-white/[0.07] rounded-xl p-6 flex items-start gap-4">
                <div
                  className="w-12 h-12 rounded-lg bg-[#CC1A1A]/10 border border-[#CC1A1A]/20 flex items-center justify-center text-[#CC1A1A] shrink-0"
                  aria-hidden="true"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div>
                  <p className="font-dm-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35 mb-1">Email</p>
                  <a
                    href="mailto:contactus@nolimitauto.co.uk"
                    className="font-fraunces text-lg font-semibold text-white hover:text-[#CC1A1A] transition-colors break-all"
                    aria-label="Email us at contactus@nolimitauto.co.uk"
                  >
                    contactus@nolimitauto.co.uk
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="bg-[#141414] border border-white/[0.07] rounded-xl p-6 flex items-start gap-4">
                <div
                  className="w-12 h-12 rounded-lg bg-[#CC1A1A]/10 border border-[#CC1A1A]/20 flex items-center justify-center text-[#CC1A1A] shrink-0"
                  aria-hidden="true"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <p className="font-dm-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-white/35 mb-1">Address</p>
                  <address className="not-italic font-dm-sans text-[15px] text-[#D1D1D1] leading-relaxed">
                    No Limit Auto Body Specialist Limited<br />
                    17 Stanley Street<br />
                    Manchester, M8 8SH<br />
                    United Kingdom
                  </address>
                </div>
              </div>

              {/* Availability */}
              <div className="bg-[#141414] border border-white/[0.07] rounded-xl p-5 flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-green-500 shrink-0" aria-hidden="true" />
                <div>
                  <span className="font-dm-sans font-semibold text-white text-[14px] block">Accepting claims today</span>
                  <span className="font-dm-sans text-[12px] text-white/40">Mon – Sat · 8AM – 6PM</span>
                </div>
              </div>
            </div>

            {/* Right — Contact form */}
            <div className="bg-[#141414] border border-white/[0.07] rounded-xl p-8">
              {submitted ? (
                <div className="relative overflow-hidden">
                  {/* Confetti particles */}
                  <div className="absolute left-1/2 top-16 -translate-x-1/2 pointer-events-none" aria-hidden="true">
                    <span className="confetti-1 absolute w-2 h-2 rounded-sm bg-[#CC1A1A] opacity-0" style={{ left: '-20px', top: 0 }} />
                    <span className="confetti-2 absolute w-1.5 h-1.5 rounded-full bg-white opacity-0" style={{ left: '4px', top: 0 }} />
                    <span className="confetti-3 absolute w-2 h-1 rounded-sm bg-[#CC1A1A] opacity-0" style={{ left: '20px', top: 0 }} />
                    <span className="confetti-4 absolute w-1.5 h-1.5 rounded-full bg-white opacity-0" style={{ left: '-6px', top: 0 }} />
                  </div>

                  {/* Success box */}
                  <div
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                    className="animate-success-fade-in bg-[#161616] border border-white/[0.08] border-l-4 border-l-[#16a34a] rounded-xl p-8 flex flex-col gap-5"
                  >
                    {/* Checkmark */}
                    <div className="flex justify-center">
                      <div
                        className="animate-checkmark-pop w-16 h-16 rounded-full bg-[#16a34a]/15 border border-[#16a34a]/40 flex items-center justify-center"
                        aria-hidden="true"
                      >
                        <svg
                          width="30"
                          height="30"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#16a34a"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                    </div>

                    {/* Text */}
                    <div className="text-center">
                      <h3 className="font-fraunces font-light text-2xl text-white mb-2">
                        Thank you! We&apos;ve received your claim.
                      </h3>
                      <p className="font-dm-sans text-[14px] text-white/55 leading-relaxed mb-4">
                        We&apos;ll call you within 1 hour. A courtesy car is reserved for you.
                      </p>

                      {/* Reference chip */}
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-white/[0.1] bg-white/[0.04] mb-4">
                        <span className="font-dm-sans text-[10px] uppercase tracking-[0.2em] text-white/35">
                          Reference
                        </span>
                        <span className="font-dm-sans text-[13px] font-semibold text-white/80 tracking-wider">
                          {orderRef}
                        </span>
                      </div>

                      {/* Phone link */}
                      <div className="flex items-center justify-center gap-2">
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#16a34a"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          aria-hidden="true"
                        >
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                        </svg>
                        <a
                          href="tel:01614001687"
                          className="font-dm-sans text-[14px] text-[#16a34a] hover:underline"
                          aria-label="Call us on 0161 400 1687"
                        >
                          0161 400 1687
                        </a>
                      </div>
                    </div>

                    {/* Reset link */}
                    <div className="text-center pt-2 border-t border-white/[0.06]">
                      <button
                        type="button"
                        onClick={resetForm}
                        className="font-dm-sans text-[12px] text-white/35 hover:text-white/65 transition-colors underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded"
                      >
                        Submit another enquiry
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  className="space-y-5"
                  aria-label="Claim and repair enquiry form"
                >
                  <h2
                    id="contact-form-heading"
                    className="font-fraunces text-2xl font-semibold text-white mb-6"
                  >
                    Send an Enquiry
                  </h2>

                  {/* Honeypot — hidden from real users, visible to bots */}
                  <div
                    style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                    aria-hidden="true"
                  >
                    <label htmlFor="website-hp-contact">Website</label>
                    <input
                      id="website-hp-contact"
                      name="website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={honeypot}
                      onChange={e => setHoneypot(e.target.value)}
                    />
                  </div>

                  {/* Error summary */}
                  {errorCount > 0 && (
                    <div
                      ref={errorSummaryRef}
                      role="alert"
                      aria-live="assertive"
                      aria-atomic="true"
                      className="flex items-start gap-3 bg-red-950/60 border border-red-500/40 rounded-lg px-4 py-3"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#f87171"
                        strokeWidth="2"
                        className="mt-0.5 shrink-0"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <circle cx="12" cy="16" r="0.5" fill="#f87171" />
                      </svg>
                      <p className="font-dm-sans text-[13px] text-red-300 leading-relaxed">
                        Please check the form —{' '}
                        <strong>
                          {errorCount} error{errorCount !== 1 ? 's' : ''} need
                          {errorCount === 1 ? 's' : ''} attention
                        </strong>
                      </p>
                    </div>
                  )}

                  {/* Name + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className={labelClass}>Full Name *</label>
                      <FieldWrapper name="name" errorId="name-error" status={fieldStatus('name')} error={errors.name}>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          autoComplete="name"
                          placeholder="e.g. James Smith"
                          value={formState.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={inputClass('name')}
                          aria-required="true"
                          aria-invalid={errors.name ? 'true' : 'false'}
                          aria-describedby="name-error"
                        />
                      </FieldWrapper>
                    </div>
                    <div>
                      <label htmlFor="phone" className={labelClass}>Phone Number *</label>
                      <FieldWrapper
                        name="phone"
                        hintId="phone-hint"
                        errorId="phone-error"
                        hint="Enter UK number like 07123 456789"
                        status={fieldStatus('phone')}
                        error={errors.phone}
                      >
                        <input
                          id="phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          inputMode="tel"
                          placeholder="07700 900 000"
                          value={formState.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={inputClass('phone')}
                          aria-required="true"
                          aria-invalid={errors.phone ? 'true' : 'false'}
                          aria-describedby="phone-hint phone-error"
                        />
                      </FieldWrapper>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className={labelClass}>Email Address *</label>
                    <FieldWrapper
                      name="email"
                      hintId="email-hint"
                      errorId="email-error"
                      hint="We'll send confirmation to this email"
                      status={fieldStatus('email')}
                      error={errors.email}
                    >
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        placeholder="you@example.com"
                        value={formState.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={inputClass('email')}
                        aria-required="true"
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-describedby="email-hint email-error"
                      />
                    </FieldWrapper>
                    {/* Email typo suggestion */}
                    {emailSuggestion && !errors.email && (
                      <div
                        role="status"
                        aria-live="polite"
                        className="mt-2 flex items-center gap-2 bg-yellow-950/50 border border-yellow-500/30 rounded-lg px-3 py-2"
                      >
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#eab308"
                          strokeWidth="2"
                          aria-hidden="true"
                          className="shrink-0"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <circle cx="12" cy="16" r="0.5" fill="#eab308" />
                        </svg>
                        <p className="font-dm-sans text-[12px] text-yellow-300 flex-1">
                          Did you mean{' '}
                          <button
                            type="button"
                            onClick={applyEmailSuggestion}
                            className="font-semibold underline hover:text-yellow-200 focus:outline-none focus:ring-1 focus:ring-yellow-400 rounded"
                          >
                            {emailSuggestion}
                          </button>
                          ? Click to use.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Enquiry Type */}
                  <div>
                    <label htmlFor="enquiryType" className={labelClass}>Enquiry Type *</label>
                    <FieldWrapper name="enquiryType" errorId="enquiryType-error" status={fieldStatus('enquiryType')} error={errors.enquiryType}>
                      <select
                        id="enquiryType"
                        name="enquiryType"
                        value={formState.enquiryType}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`${inputClass('enquiryType')} cursor-pointer`}
                        aria-required="true"
                        aria-invalid={errors.enquiryType ? 'true' : 'false'}
                        aria-describedby="enquiryType-error"
                      >
                        <option value="" disabled>Select enquiry type...</option>
                        <option value="non-fault-claim">Non-Fault Claim</option>
                        <option value="repair-estimate">Repair Estimate</option>
                        <option value="module-coding">Module Coding</option>
                        <option value="srs-airbag">SRS / Airbag Repair</option>
                        <option value="chassis-alignment">Chassis Alignment</option>
                        <option value="general">General Enquiry</option>
                      </select>
                    </FieldWrapper>
                  </div>

                  {/* Registration + Make */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="registration" className={labelClass}>Vehicle Registration *</label>
                      <FieldWrapper
                        name="registration"
                        hintId="registration-hint"
                        errorId="registration-error"
                        hint="Enter reg like AB12 CDE"
                        status={fieldStatus('registration')}
                        error={errors.registration}
                      >
                        <input
                          id="registration"
                          name="registration"
                          type="text"
                          autoComplete="off"
                          placeholder="e.g. AB71 XYZ"
                          value={formState.registration}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={`${inputClass('registration')} uppercase`}
                          aria-required="true"
                          aria-invalid={errors.registration ? 'true' : 'false'}
                          aria-describedby="registration-hint registration-error"
                        />
                      </FieldWrapper>
                    </div>
                    <div>
                      <label htmlFor="vehicleMake" className={labelClass}>Vehicle Make &amp; Model</label>
                      <input
                        id="vehicleMake"
                        name="vehicleMake"
                        type="text"
                        placeholder="e.g. BMW 3 Series"
                        value={formState.vehicleMake}
                        onChange={handleChange}
                        className={`w-full bg-[#141414] border border-white/[0.1] rounded-lg px-4 py-3.5 text-white font-dm-sans text-[15px] placeholder:text-white/25 focus:border-[#CC1A1A] focus:outline-none transition-colors min-h-[52px]`}
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className={labelClass}>Message</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Please describe your enquiry or the damage to your vehicle..."
                      value={formState.message}
                      onChange={handleChange}
                      className="w-full bg-[#141414] border border-white/[0.1] rounded-lg px-4 py-3.5 text-white font-dm-sans text-[15px] placeholder:text-white/25 focus:border-[#CC1A1A] focus:outline-none transition-colors resize-none min-h-[52px]"
                    />
                  </div>

                  {/* GDPR */}
                  <div className="pt-2">
                    <label htmlFor="gdpr" className="flex items-start gap-3 cursor-pointer min-h-[44px]">
                      <input
                        id="gdpr"
                        name="gdpr"
                        type="checkbox"
                        checked={formState.gdpr}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className="mt-1 w-5 h-5 rounded border-white/20 bg-[#1a1a1a] accent-[#CC1A1A] cursor-pointer shrink-0"
                        aria-required="true"
                        aria-invalid={errors.gdpr ? 'true' : 'false'}
                        aria-describedby="gdpr-error"
                      />
                      <span className="font-dm-sans text-[13px] text-white/45 leading-relaxed">
                        I consent to No Limit Auto Body Specialist storing my details to process my
                        enquiry, in accordance with the{' '}
                        <Link href="/privacy-policy" className="text-[#CC1A1A] hover:underline">
                          Privacy Policy
                        </Link>
                        .
                      </span>
                    </label>
                    {errors.gdpr && (
                      <p id="gdpr-error" role="alert" className="font-dm-sans text-[12px] text-red-400 mt-1">
                        {errors.gdpr}
                      </p>
                    )}
                  </div>

                  {/* Submit error */}
                  {submitError && (
                    <div
                      role="alert"
                      className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#f87171"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                        className="shrink-0 mt-0.5"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <circle cx="12" cy="16" r="0.5" fill="#f87171" />
                      </svg>
                      <div className="flex flex-col gap-1">
                        <p className="font-dm-sans text-[12px] text-red-400 leading-relaxed">
                          {submitError}
                        </p>
                        <p className="font-dm-sans text-[12px] text-white/50 leading-relaxed">
                          Having trouble sending?{' '}
                          <a
                            href="tel:01614001687"
                            className="text-white/70 underline underline-offset-2 hover:text-white transition-colors"
                          >
                            Call us directly on 0161 400 1687
                          </a>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Rate limit message */}
                  {rateLimitMsg && (
                    <p className="font-dm-sans text-[13px] text-yellow-400 bg-yellow-900/20 border border-yellow-500/30 rounded-lg px-4 py-3">
                      {rateLimitMsg}
                    </p>
                  )}

                  {/* Retry status */}
                  {retryStatus && !submitError && (
                    <p className="font-dm-sans text-[12px] text-white/40 text-center" aria-live="polite">
                      {retryStatus}
                    </p>
                  )}

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={submitting || submitSuccess}
                    aria-label="Submit your enquiry"
                    className={`w-full mt-2 px-6 py-4 font-dm-sans font-bold text-[14px] uppercase tracking-[0.15em] transition-all duration-200 rounded-lg min-h-[56px] disabled:cursor-not-allowed flex items-center justify-center gap-3 ${
                      submitSuccess
                        ? 'bg-[#16a34a] text-white'
                        : 'bg-[#CC1A1A] text-white hover:bg-[#E02020] active:bg-[#B01515]'
                    }`}
                  >
                    {submitting ? (
                      <>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="animate-spin"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                          <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                        </svg>
                        {retryStatus || 'Sending…'}
                      </>
                    ) : submitSuccess ? (
                      <>
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          aria-hidden="true"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Sent! ✓
                      </>
                    ) : (
                      'Send Enquiry'
                    )}
                  </button>

                  {/* Security badges + response time guarantee */}
                  <div className="mt-5 pt-5 border-t border-white/[0.07]">
                    <div className="flex items-center justify-center gap-2 mb-3">
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#4ade80"
                        strokeWidth="2"
                        strokeLinecap="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span className="font-dm-sans text-[12px] text-white/40">
                        We respond within{' '}
                        <span className="text-white/65 font-semibold">2 business hours</span> ·
                        Mon–Sat 8AM–6PM
                      </span>
                    </div>
                    <div
                      className="flex items-center justify-center gap-3 flex-wrap"
                      role="list"
                      aria-label="Security and compliance badges"
                    >
                      <div
                        role="listitem"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.03]"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                          <path d="M12 2a10 10 0 0 1 10 10" />
                        </svg>
                        <span className="font-dm-sans text-[11px] font-semibold uppercase tracking-[0.15em] text-white/45">SSL Secured</span>
                      </div>
                      <div
                        role="listitem"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.03]"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        </svg>
                        <span className="font-dm-sans text-[11px] font-semibold uppercase tracking-[0.15em] text-white/45">GDPR Compliant</span>
                      </div>
                      <div
                        role="listitem"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.03]"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C8A96E" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                        <span className="font-dm-sans text-[11px] font-semibold uppercase tracking-[0.15em] text-white/45">Trusted Partner</span>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
